import React from 'react';
import Button from '@mui/material/Button';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';

const DAY = 86400000;

// Explains what to fix when the site's address failed the website check (DNS + SSL).
// Warning only: the site and its popups keep working.
export const urlCheckMessage = (site) => {
  const check = site.url_check;
  if (!check || !['failed', 'warning'].includes(check.status)) {
    return null;
  }

  const url = site.url;

  if (check.dns === 'failed') {
    // An approved site was let through on purpose (e.g. by an admin), so don't tell its owner it can't be found
    if (site.review_status === 'approved') {
      return null;
    }
    return `We couldn't find ${url} on the internet. Check that the website address is spelled correctly and that the site is live.`;
  }

  switch (check.ssl) {
    case 'missing':
      return `${url} doesn't have an SSL certificate, so it can't be visited securely over https://. Ask your hosting provider to set one up.`;
    case 'self_signed':
      return `${url} uses a self-signed SSL certificate, which browsers don't trust. Replace it with a certificate from a trusted provider, such as Let's Encrypt.`;
    case 'expired':
      return `The SSL certificate for ${url} has expired. Renew it with your hosting provider so visitors don't see security warnings.`;
    case 'hostname_mismatch':
      return `The SSL certificate on ${url} was issued for a different domain. Make sure your certificate covers ${url}.`;
    case 'invalid':
      return `The SSL certificate for ${url} couldn't be verified. Check with your hosting provider that it's installed correctly.`;
    case 'expiring_soon':
      return `The SSL certificate for ${url} expires on ${moment(check.ssl_expires_at).format('MMM D, YYYY')}. Renew it soon so visitors don't see security warnings.`;
    default:
      return null;
  }
};

export default function UrlCheckWarning({ site }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();

  const message = urlCheckMessage(site);
  const skipKey = `UrlCheckWarning.Skip.${site.id}`;
  const skipTime = Number(sessionStorage.getItem(skipKey));

  if (!message || skipTime > new Date().getTime() - DAY) {
    return '';
  }

  const skip = (key) => () => {
    sessionStorage.setItem(skipKey, new Date().getTime().toString());
    closeSnackbar(key);
  };

  const action = (key) => (
    <React.Fragment>
      {site.url_check.dns === 'failed' && (
        <Button
          onClick={() => {
            history.push(`/sites/${site.id}/settings/site`);
            closeSnackbar(key);
          }}
        >
          Update Address
        </Button>
      )}
      <Button onClick={skip(key)}>Dismiss</Button>
    </React.Fragment>
  );

  setTimeout(() => {
    enqueueSnackbar(message, {
      action,
      persist: true,
      preventDuplicate: true,
      key: `url_check_${site.id}`,
      variant: 'warning',
    });
  }, 1);

  return '';
}
